import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import statisticsService from "./statisticsService";
import { DEFAULT_STATISTICS_PERIOD } from "./statistics.types";

const initialState = {
  meetings: [],
  transcripts: [],
  assemblyAI: [],
  connections: [],
  connectionSummary: {
    totalConnections: 0,
    totalConnectionRequests: 0,
  },
  period: DEFAULT_STATISTICS_PERIOD,
  fetchedAt: null,
  meetingsFetchedAt: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: "",
};

const CACHE_TTL_MS = 3 * 60 * 1000;

const buildCachedStatisticsPayload = (state) => ({
  meetings: state.meetings || [],
  transcripts: state.transcripts || [],
  assemblyAI: state.assemblyAI || [],
  connections: state.connections || [],
  connectionSummary: state.connectionSummary || initialState.connectionSummary,
  fetchedAt: state.fetchedAt || Date.now(),
});

const buildCachedMeetingsPayload = (state) => ({
  meetings: state.meetings || [],
  fetchedAt: state.meetingsFetchedAt || state.fetchedAt || Date.now(),
});

export const listStatisticsData = createAsyncThunk("statistics/list", async (options = {}, thunkAPI) => {
  try {
    const force = Boolean(options?.force);
    const statsState = thunkAPI.getState()?.statistics;
    const hasCache = Number.isFinite(statsState?.fetchedAt) && (Date.now() - statsState.fetchedAt) < CACHE_TTL_MS;
    const hasData =
      (statsState?.meetings?.length || 0) > 0 ||
      (statsState?.transcripts?.length || 0) > 0 ||
      (statsState?.assemblyAI?.length || 0) > 0;

    if (!force && hasCache && hasData) {
      return buildCachedStatisticsPayload(statsState);
    }

    return await statisticsService.listStatisticsData();
  } catch (error) {
    return thunkAPI.rejectWithValue(error?.message || "İstatistikler yüklenemedi.");
  }
});

export const listMeetingStatisticsData = createAsyncThunk("statistics/listMeetings", async (options = {}, thunkAPI) => {
  try {
    const force = Boolean(options?.force);
    const statsState = thunkAPI.getState()?.statistics;
    const cacheAt = statsState?.meetingsFetchedAt || statsState?.fetchedAt;
    const hasCache = Number.isFinite(cacheAt) && (Date.now() - cacheAt) < CACHE_TTL_MS;
    const hasData = (statsState?.meetings?.length || 0) > 0;

    if (!force && hasCache && hasData) {
      return buildCachedMeetingsPayload(statsState);
    }

    return await statisticsService.listMeetingStatisticsData();
  } catch (error) {
    return thunkAPI.rejectWithValue(error?.message || "Toplantı istatistikleri yüklenemedi.");
  }
});

const statisticsSlice = createSlice({
  name: "statistics",
  initialState,
  reducers: {
    resetStatisticsState: () => initialState,
    setStatisticsPeriod: (state, action) => {
      state.period = action.payload || DEFAULT_STATISTICS_PERIOD;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listStatisticsData.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = "";
      })
      .addCase(listStatisticsData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.meetings = action.payload?.meetings || [];
        state.transcripts = action.payload?.transcripts || [];
        state.assemblyAI = action.payload?.assemblyAI || [];
        state.connections = action.payload?.connections || [];
        state.connectionSummary = action.payload?.connectionSummary || initialState.connectionSummary;
        state.fetchedAt = action.payload?.fetchedAt || Date.now();
        state.meetingsFetchedAt = action.payload?.fetchedAt || Date.now();
      })
      .addCase(listStatisticsData.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = true;
        state.message = action.payload || "İstatistikler yüklenemedi.";
      })
      .addCase(listMeetingStatisticsData.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.message = "";
      })
      .addCase(listMeetingStatisticsData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.meetings = action.payload?.meetings || [];
        state.meetingsFetchedAt = action.payload?.fetchedAt || Date.now();
      })
      .addCase(listMeetingStatisticsData.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = true;
        state.message = action.payload || "Toplantı istatistikleri yüklenemedi.";
      });
  },
});

export const { resetStatisticsState, setStatisticsPeriod } = statisticsSlice.actions;

export default statisticsSlice.reducer;
